<template>
  <Multiselector
    v-if="options.length"
    v-bind="$attrs"
    :id="getRandomId('institutions-select')"
    :model-value="selectedValues"
    :options="options"
    :placeholder="$t('Choose institutions')"
    :main-input-label="hideLabel ? '' : $t(multiple ? 'Institutions' : 'Institution')"
    :multiple="multiple"
    @update:model-value="onChange"
  />
</template>

<script>
import Multiselector from "@/components/Multiselector.vue";
import { mapGetters } from "vuex";
import { getRandomId } from "@/utils/string";

export default {
  name: "InstitutionsSelect",

  components: {
    Multiselector,
  },

  props: {
    modelValue: {
      type: [Number, Array, undefined],
      default: null,
    },

    multiple: {
      type: Boolean,
      default: false,
    },

    placeholder: {
      type: String,
      default: "all",
      validator: function (value) {
        return ["all", "select"].includes(value);
      },
    },

    showPlaceholder: {
      type: Boolean,
      default: true,
    },

    hideLabel: {
      type: Boolean,
      default: false,
    },

    institutionsToSkip: {
      type: Array,
      default: () => [],
    },

    defaultOptions: {
      type: Array,
      default: null,
    },

    showOnlyFromDistrict: {
      type: Number,
      default: null,
    },

    autoSelectFirst: {
      type: Boolean,
      default: false,
    },

    showAll: {
      type: Boolean,
      default: null,
    },

    hiddenForUsers: {
      type: Boolean,
      default: null,
    },

    includeInactive: {
      type: Boolean,
      default: false,
    },

    disablePlaceholder: {
      type: Boolean,
      default: false,
    },

    // Prevents the fallback dispatch("institutions/getOptions") when defaultOptions is empty.
    // Needed on public pages (e.g. registration) where the user has no permission to call
    // that authenticated endpoint - falling back would trigger a 401 and force a logout.
    disableSelfFetch: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["update:modelValue"],

  data() {
    return {
      allOptions: [],
    };
  },

  computed: {
    ...mapGetters({
      showShortName: "institutions/showShortNames",
      isAuthenticated: "auth/isAuthenticated",
    }),

    placeholderText() {
      return this.placeholder === "all" ? this.$t("all") : this.$t("Select institution");
    },

    options() {
      let options = this.allOptions;

      if (this.institutionsToSkip.length) {
        options = options.filter((item) => !this.institutionsToSkip.includes(item.value));
      }

      if (this.showOnlyFromDistrict) {
        options = options.filter((item) => this.showOnlyFromDistrict === item.districtId);
      }

      options = options.map((item) => ({
        value: item.value,
        text: (this.showShortName ? item.shortName : item.text) || item.text,
      }));

      if (this.showPlaceholder && !this.multiple && !this.defaultOptions?.length) {
        options.unshift({
          value: null,
          text: this.placeholderText,
          disabled: this.disablePlaceholder,
        });
      }

      return options;
    },

    selectedValues() {
      return this.modelValue ? (this.multiple ? this.modelValue : [this.modelValue]) : [];
    },
  },

  watch: {
    defaultOptions(newVal) {
      if (newVal) {
        this.allOptions = newVal;
      }
    },
  },

  async mounted() {
    if (this.defaultOptions) {
      this.allOptions = this.defaultOptions;
    } else if (!this.disableSelfFetch && this.isAuthenticated) {
      this.allOptions = await this.$store.dispatch("institutions/getOptions", {
        showAll: this.showAll ?? undefined,
        hiddenForUsers: this.hiddenForUsers !== null ? this.hiddenForUsers : undefined,
        includeInactive: this.includeInactive || undefined,
      });
    }

    const hasSelectedValue = Array.isArray(this.modelValue)
      ? this.modelValue.length
      : this.modelValue;

    if (this.autoSelectFirst && !hasSelectedValue && this.options?.length) {
      this.onChange([this.options[0].value]);
    }
  },

  methods: {
    getRandomId,

    onChange(values) {
      if (!this.multiple && Array.isArray(values)) {
        values = values[0];
      }

      this.$emit("update:modelValue", values);
    },
  },
};
</script>
